import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { modbusSettings } from '$lib/PersistedState.svelte';
import { fahrenheitToCelsius } from '$lib/RLogUtils';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class DiedrichDevice extends BaseDevice {
  type: DeviceType = DeviceType.DIEDRICH;
  private fuelLevel: number | null = null;
  private airLevel: number | null = null;

  protected async onConnect(): Promise<boolean> {
    try {
      await invoke('connect_modbus', {
        host: modbusSettings.current.host,
        port: this.getModbusPort()
      });
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Diedrich device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_modbus', {
      host: modbusSettings.current.host,
      port: this.getModbusPort()
    }).catch((error) => logger.warn(`Disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      // Read all registers at once (0-7, which covers all data)
      const results = await this.readModbusRegisters(modbusSettings.current.host, 0, 8);

      // Extract and convert the values
      const bean = DiedrichDevice.convertRegistersToFloat(results[0], results[1]); // registers 0-1
      const inlet = DiedrichDevice.convertRegistersToFloat(results[2], results[3]); // registers 2-3
      const outlet = DiedrichDevice.convertRegistersToFloat(results[4], results[5]); // registers 4-5
      const fuelLevel = results[6]; // register 6
      const airLevel = results[7]; // register 7

      this.fuelLevel = fuelLevel;
      this.airLevel = airLevel;

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_DIEDRICH_READINGS', {
          allResults: results,
          bean,
          inlet,
          outlet,
          fuelLevel: this.fuelLevel,
          airLevel: this.airLevel,
          // Debug the raw register pairs
          beanRaw: [results[0], results[1]],
          inletRaw: [results[2], results[3]],
          outletRaw: [results[4], results[5]]
        });
      }

      // Diedrich returns temperatures in F, convert to C
      this.localReadings = [
        fahrenheitToCelsius(bean), // Bean temp
        this.tcSettings?.[1].active ? fahrenheitToCelsius(inlet) : null, // Inlet
        this.tcSettings?.[2].active ? fahrenheitToCelsius(outlet) : null, // Exhaust
        null // TC4 not used for Diedrich
      ];
    } catch (error) {
      logger.error(`Diedrich Modbus error: ${error}`);
      throw error;
    }
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.fuelLevel;
  }

  /**
   * Get the current air level percentage
   */
  public getAirLevel(): number | null {
    return this.airLevel;
  }

  private async readModbusRegisters(
    host: string,
    startRegister: number,
    count: number
  ): Promise<number[]> {
    try {
      return await invoke('read_modbus', {
        host,
        port: this.getModbusPort(),
        reg: startRegister,
        count
      });
    } catch (error) {
      throw new Error(
        `Failed to read Modbus registers ${startRegister}-${startRegister + count - 1}: ${error}`
      );
    }
  }

  public static convertRegistersToFloat(highRegister: number, lowRegister: number): number {
    // Convert two 16-bit registers to a 32-bit float
    // Based on testing, the Diedrich device uses little-endian register order
    // (low register first, high register second) with big-endian byte order

    // Combine the two 16-bit registers into a 32-bit integer
    // Low register is the most significant word, high register is the least significant
    const combined = (lowRegister << 16) | (highRegister & 0xffff);

    // Create a DataView to interpret the 32-bit integer as a float
    const buffer = new ArrayBuffer(4);
    const view = new DataView(buffer);
    view.setUint32(0, combined, false); // false = big-endian

    // Read as float32
    return view.getFloat32(0, false); // false = big-endian
  }
}

export default DiedrichDevice;
